<template>
    <div>
        <!-- Current item -->
        <div
            v-if="item._isVisible"
            :class="[
                'hierarchy-item',
                `hierarchy-item--level-${level}`,
                {
                    'multi-selected': selectionState !== 'unselected',
                },
            ]"
            role="button"
            tabindex="0"
            @click.stop="$emit('toggle-option', item.id)"
        >
            <div class="flex items-center gap-3">
                <div class="flex-shrink-0">
                    <div
                        :class="[
                            'hierarchy-checkbox',
                            {
                                'hierarchy-checkbox--checked': selectionState === 'selected',
                                'hierarchy-checkbox--indeterminate': selectionState === 'indeterminate',
                            },
                        ]"
                    >
                        <icon v-if="selectionState === 'selected'" icon="cil-check-alt" size="sm" />
                        <icon v-else-if="selectionState === 'indeterminate'" icon="cil-minus" size="sm" />
                    </div>
                </div>
                <div class="flex min-w-0 flex-1 flex-col">
                    <div class="flex min-w-0 items-center gap-2">
                        <span class="hierarchy-item__name truncate">{{ item.name }}</span>
                        <Badge v-if="item.badge" color="secondary">{{ item.badge }}</Badge>
                        <Badge v-if="item.iataCode" color="secondary">{{ item.iataCode }}</Badge>
                    </div>
                    <span v-if="item.subtitle" class="hierarchy-item__subtitle truncate">{{ item.subtitle }}</span>
                </div>
                <div class="flex-shrink-0">
                    <button
                        v-if="item.children && item.children.length > 0"
                        type="button"
                        class="d-flex align-items-center justify-content-center p-1 text-primary"
                        @click.stop="$emit('toggle-collapse', item.id)"
                    >
                        <ArrowDown class="hierarchy-toggle" :class="{ 'hierarchy-toggle--collapsed': collapsedItems.has(item.id) }" />
                    </button>
                </div>
            </div>
        </div>

        <!-- Children (recursive) -->
        <!-- Use v-show during search for instant display, Collapse for smooth animations when not searching -->
        <div v-if="isSearching" v-show="item._shouldShowChildren" class="ml-6">
            <hierarchy-node
                v-for="child in item.children"
                :key="child.id"
                :item="child"
                :level="level + 1"
                :selected="selected"
                :collapsed-items="collapsedItems"
                :get-selection-state="getSelectionState"
                :is-searching="isSearching"
                @toggle-option="$emit('toggle-option', $event)"
                @toggle-collapse="$emit('toggle-collapse', $event)"
            />
        </div>
        <collapse v-else :show="item._shouldShowChildren">
            <div class="ml-6 pl-2">
                <hierarchy-node
                    v-for="child in item.children"
                    :key="child.id"
                    :item="child"
                    :level="level + 1"
                    :selected="selected"
                    :collapsed-items="collapsedItems"
                    :get-selection-state="getSelectionState"
                    :is-searching="isSearching"
                    @toggle-option="$emit('toggle-option', $event)"
                    @toggle-collapse="$emit('toggle-collapse', $event)"
                />
            </div>
        </collapse>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import Icon from '@/Components/Icon.vue';
import Collapse from '@/Components/Collapse.vue';
import ArrowDown from '@/../icons/ArrowDown.vue';
import Badge from '@/Components/Badge.vue';

// Props
const props = defineProps({
    item: {
        type: Object,
        required: true,
    },
    level: {
        type: Number,
        default: 0,
    },
    selected: {
        type: Array,
        default: () => [],
    },
    collapsedItems: {
        type: Set,
        required: true,
    },
    getSelectionState: {
        type: Function,
        required: true,
    },
    isSearching: {
        type: Boolean,
        default: false,
    },
});

defineEmits(['toggle-option', 'toggle-collapse']);

const selectionState = computed(() => {
    return props.getSelectionState(props.item);
});
</script>
<style scoped>
.hierarchy-item {
    cursor: pointer;
    transition: all 0.15s ease;
}

.hierarchy-item:hover {
    background-color: #ebedef;
}

.hierarchy-item--level-0 {
    padding: 10px 12px;
    border-bottom: 1px solid #f0f0f0;
}

.hierarchy-item--level-1 {
    padding: 8px 12px;
}

.hierarchy-item--level-2 {
    padding: 6px 12px;
}

.hierarchy-item[class*='hierarchy-item--level-']:not(.hierarchy-item--level-0):not(.hierarchy-item--level-1):not(.hierarchy-item--level-2) {
    padding: 4px 12px;
}

.hierarchy-item__name {
    color: #2c3e50;
    line-height: 1.5;
}

.hierarchy-item__subtitle {
    color: #8a93a2;
    font-size: 0.8125rem;
    line-height: 1.2;
}

.hierarchy-checkbox {
    width: 18px;
    height: 18px;
    border: 2px solid #dee2e6;
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: white;
    transition: all 0.15s ease;
}

.hierarchy-checkbox--checked {
    background-color: var(--color-primary);
    border-color: var(--color-primary);
    color: white;
}

.hierarchy-checkbox--indeterminate {
    background-color: var(--color-primary);
    border-color: var(--color-primary);
    color: white;
}

.hierarchy-toggle {
    color: var(--color-primary);
}

.hierarchy-toggle--collapsed {
    transform: rotate(-90deg);
}
</style>
